import { Injectable, inject, signal } from '@angular/core';
import { HttpRequest, HttpHeaders, HttpClient } from '@angular/common/http';
import { Observable, Subject, from, of, EMPTY, firstValueFrom } from 'rxjs';
import { catchError, switchMap, tap, delay, retryWhen, take } from 'rxjs/operators';
import { NetworkService } from './network.service';
import { LoggerService } from './logger.service';
import { ApiConfigService } from './api-config.service';

export interface QueuedRequest {
  id: string;
  method: string;
  url: string;
  body?: any;
  headers: Record<string, string>;
  timestamp: number;
  retryCount: number;
  priority: 'critical' | 'normal' | 'low';
  maxRetries?: number;
}

const STORAGE_KEY = 'cartco.offlineQueue';
const MAX_QUEUE_SIZE = 100;
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000; // 1 second base delay

@Injectable({ providedIn: 'root' })
export class OfflineQueueService {
  private readonly networkService = inject(NetworkService);
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfigService);
  private readonly logger = inject(LoggerService);

  private readonly queue = signal<QueuedRequest[]>([]);
  private readonly isSyncing = signal<boolean>(false);
  private readonly syncSubject = new Subject<void>();

  readonly queueSize = this.queue.asReadonly();
  readonly syncing = this.isSyncing.asReadonly();

  constructor() {
    this.loadQueueFromStorage();
    this.setupAutoSync();
  }

  /**
   * Queue a failed HTTP request for retry when online
   */
  queueRequest(
    request: HttpRequest<any>,
    priority: 'critical' | 'normal' | 'low' = 'normal',
    maxRetries: number = MAX_RETRIES
  ): string {
    const queuedRequest: QueuedRequest = {
      id: this.generateId(),
      method: request.method || 'GET',
      url: request.url,
      body: request.body,
      headers: this.serializeHeaders(request.headers),
      timestamp: Date.now(),
      retryCount: 0,
      priority,
      maxRetries,
    };

    const currentQueue = this.queue();
    
    // Check queue size limit
    if (currentQueue.length >= MAX_QUEUE_SIZE) {
      // Remove oldest low-priority request
      const lowPriorityIndex = currentQueue.findIndex((q) => q.priority === 'low');
      if (lowPriorityIndex !== -1) {
        currentQueue.splice(lowPriorityIndex, 1);
      } else {
        // If no low-priority, remove oldest normal-priority
        const normalPriorityIndex = currentQueue.findIndex((q) => q.priority === 'normal');
        if (normalPriorityIndex !== -1) {
          currentQueue.splice(normalPriorityIndex, 1);
        } else {
          // Last resort: remove oldest
          currentQueue.shift();
        }
      }
    }

    // Add new request (critical first, then by timestamp)
    const updatedQueue = [...currentQueue, queuedRequest].sort((a, b) => {
      const priorityOrder = { critical: 0, normal: 1, low: 2 };
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      if (priorityDiff !== 0) return priorityDiff;
      return a.timestamp - b.timestamp;
    });

    this.queue.set(updatedQueue);
    this.saveQueueToStorage();

    this.logger.debug(`[OfflineQueue] Queued request: ${queuedRequest.method} ${queuedRequest.url}`, {
      priority,
      queueSize: updatedQueue.length,
    });

    // If online, trigger sync
    if (this.networkService.isCurrentlyOnline()) {
      this.syncQueue();
    }

    return queuedRequest.id;
  }

  /**
   * Remove a request from the queue
   */
  removeRequest(requestId: string): void {
    const updatedQueue = this.queue().filter((q) => q.id !== requestId);
    this.queue.set(updatedQueue);
    this.saveQueueToStorage();
  }

  /**
   * Clear all queued requests
   */
  clearQueue(): void {
    this.queue.set([]);
    this.saveQueueToStorage();
  }

  /**
   * Get all queued requests
   */
  getQueuedRequests(): QueuedRequest[] {
    return [...this.queue()];
  }

  /**
   * Manually trigger queue sync
   */
  async syncQueue(): Promise<void> {
    if (this.isSyncing()) {
      this.logger.debug('[OfflineQueue] Sync already in progress');
      return;
    }

    if (!this.networkService.isCurrentlyOnline()) {
      this.logger.debug('[OfflineQueue] Cannot sync - offline');
      return;
    }

    const queuedRequests = this.queue();
    if (queuedRequests.length === 0) {
      return;
    }

    this.isSyncing.set(true);
    this.logger.debug(`[OfflineQueue] Starting sync of ${queuedRequests.length} requests`);

    try {
      // Process requests in order (already sorted by priority)
      for (const queuedRequest of queuedRequests) {
        if (!this.networkService.isCurrentlyOnline()) {
          this.logger.debug('[OfflineQueue] Went offline during sync');
          break;
        }

        try {
          await this.processRequest(queuedRequest);
          this.removeRequest(queuedRequest.id);
        } catch (error) {
          this.logger.error(`[OfflineQueue] Failed to process request ${queuedRequest.id}`, error);
          
          // Increment retry count
          queuedRequest.retryCount++;
          
          // Remove if max retries exceeded
          if (queuedRequest.retryCount >= (queuedRequest.maxRetries || MAX_RETRIES)) {
            this.logger.warn(
              `[OfflineQueue] Max retries exceeded for request ${queuedRequest.id}, removing from queue`
            );
            this.removeRequest(queuedRequest.id);
          } else {
            // Update queue with new retry count
            const updatedQueue = this.queue().map((q) =>
              q.id === queuedRequest.id ? queuedRequest : q
            );
            this.queue.set(updatedQueue);
            this.saveQueueToStorage();
          }
        }
      }
    } finally {
      this.isSyncing.set(false);
      this.syncSubject.next();
    }
  }

  private async processRequest(queuedRequest: QueuedRequest): Promise<void> {
    // Reconstruct HTTP request URL (remove base URL if present)
    let url = queuedRequest.url;
    if (url.startsWith('http')) {
      // Full URL, use as is
    } else {
      // Relative URL, prepend base URL
      url = `${this.apiConfig.getApiUrl()}/${url.replace(/^\//, '')}`;
    }

    const headers = new HttpHeaders(queuedRequest.headers);
    
    // Execute request with exponential backoff
    // For first attempt (retryCount = 0), use minimal delay in tests
    const delayMs = queuedRequest.retryCount === 0 ? 10 : RETRY_DELAY_MS * Math.pow(2, queuedRequest.retryCount);
    
    await new Promise((resolve) => setTimeout(resolve, delayMs));

    let request$: Observable<any>;
    
    switch (queuedRequest.method.toUpperCase()) {
      case 'GET':
        request$ = this.http.get(url, { headers });
        break;
      case 'POST':
        request$ = this.http.post(url, queuedRequest.body, { headers });
        break;
      case 'PUT':
        request$ = this.http.put(url, queuedRequest.body, { headers });
        break;
      case 'PATCH':
        request$ = this.http.patch(url, queuedRequest.body, { headers });
        break;
      case 'DELETE':
        request$ = this.http.delete(url, { headers });
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${queuedRequest.method}`);
    }

    try {
      await firstValueFrom(request$);
      this.logger.debug(`[OfflineQueue] Successfully processed request ${queuedRequest.id}`);
    } catch (error) {
      throw error;
    }
  }

  private setupAutoSync(): void {
    // Sync when network comes back online
    this.networkService.online$.subscribe((isOnline) => {
      if (isOnline && this.queue().length > 0) {
        this.logger.debug('[OfflineQueue] Network online, triggering sync');
        // Small delay to ensure network is stable
        setTimeout(() => this.syncQueue(), 500);
      }
    });
  }

  private serializeHeaders(headers: HttpHeaders): Record<string, string> {
    const serialized: Record<string, string> = {};
    headers.keys().forEach((key) => {
      const value = headers.get(key);
      if (value) {
        serialized[key] = value;
      }
    });
    return serialized;
  }

  private generateId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private loadQueueFromStorage(): void {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as QueuedRequest[];
          // Filter out requests older than 24 hours
          const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
          const validRequests = parsed.filter((req) => req.timestamp > twentyFourHoursAgo);
          this.queue.set(validRequests);
          
          if (validRequests.length !== parsed.length) {
            this.saveQueueToStorage(); // Save filtered queue
          }
        }
      }
    } catch (error) {
      this.logger.error('[OfflineQueue] Error loading queue from storage', error);
    }
  }

  private saveQueueToStorage(): void {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.queue()));
      }
    } catch (error) {
      this.logger.error('[OfflineQueue] Error saving queue to storage', error);
    }
  }
}

